import type { ExtractedTaxSummary } from "@/types/tax";
import { makeId, parseMoney } from "@/lib/utils";

export async function extractPdfText(file: File) {
  const pdfjs = await import("pdfjs-dist");
  pdfjs.GlobalWorkerOptions.workerSrc = new URL(
    "pdfjs-dist/build/pdf.worker.mjs",
    import.meta.url,
  ).toString();

  const data = await file.arrayBuffer();
  const loadingTask = pdfjs.getDocument({ data });
  const pdf = await loadingTask.promise;
  const pages: string[] = [];

  for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
    const page = await pdf.getPage(pageNumber);
    const content = await page.getTextContent();
    pages.push(textItemsToLines(content.items));
  }

  return pages.join("\n");
}

export function parseTaxSummaryText(rawText: string, fileName = "Manual entry"): ExtractedTaxSummary {
  const lines = normalizeLines(rawText);
  const text = normalizeText(rawText);
  const provider = detectProvider(text, fileName);
  const period = parsePeriod(text, fileName);
  const didiGrossRiderFares = provider === "didi" ? pickFirstAmountAfterLabel(lines, /gross\s+rider\s+fares/i) : 0;
  const grossFares = didiGrossRiderFares || pickFieldAmount(lines, text, [
    /total\s+transportation\s+income/i,
    /transportation\s+income/i,
    /gross\s+rider\s+fares/i,
    /gross\s+transportation\s+fare/i,
    /gross\s+(?:passenger\s+)?fares?/i,
    /passenger\s+fares?/i,
    /total\s+fare(?:s)?/i,
    /fare\s+total/i,
    /gross\s+trip/i,
    /total\s+sales/i,
    /rideshare\s+services/i,
    /trip\s+fare/i,
    /gross\s+earnings/i,
    /total\s+income/i,
  ]);
  const didiItemizedDeductions = provider === "didi" ? sumFieldAmounts(lines, [
    /didi\s+service\s+fee/i,
    /airport\s+fee/i,
    /government\s+rideshare\s+levy/i,
    /other\s+deductions/i,
  ]) : 0;
  const totalDeductions = didiItemizedDeductions || pickFieldAmount(lines, text, [
    /total\s+potential\s+tax\s+deductions/i,
    /total\s+deductions/i,
  ]);
  const itemizedDeductions = sumFieldAmounts(lines, [
    /uber\s+service\s+fee/i,
    /other\s+charges\s+from\s+(?:uber|platform)/i,
    /charges\s+from\s+3rd\s+parties/i,
  ]);
  const platformFeesGross = totalDeductions || itemizedDeductions || pickFieldAmount(lines, text, [
    /uber\s+service\s+fee/i,
    /service\s+fee/i,
    /platform\s+fee/i,
    /commission/i,
    /didi\s+fee/i,
    /booking\s+fee/i,
    /other\s+charges\s+from\s+(?:uber|platform)/i,
  ]);
  const explicitPlatformGst = pickFieldAmount(lines, text, [
    /gst\s+on\s+(?:service|platform|commission|booking)\s+fees?/i,
    /service\s+fee\s+gst/i,
    /platform\s+fee\s+gst/i,
    /commission\s+gst/i,
    /gst\s+credit/i,
  ]);
  const tollsGross = pickFieldAmount(lines, text, [
    /tolls?\s+reimbursement/i,
    /road\s+tolls?/i,
    /\btolls?\b/i,
  ]);
  const tipsAndBonuses = pickFieldAmount(lines, text, [
    /tips?\s+and\s+bonuses/i,
    /referral\s*\/\s*incentives/i,
    /incentives?/i,
    /bonuses?/i,
    /\btips?\b/i,
  ]);

  const notes: string[] = [];
  if (grossFares === 0) notes.push("Gross fares were not confidently found. Enter the full fare total before accepting.");
  if (platformFeesGross > 0 && explicitPlatformGst === 0) {
    notes.push("GST on deductions was estimated as one-eleventh of total deductions because the PDF does not show a separate GST amount. Please review before saving.");
  }

  const confidence = grossFares > 0 && period.periodStart && period.periodEnd ? "medium" : "low";

  return {
    id: makeId("extract"),
    provider,
    periodStart: period.periodStart,
    periodEnd: period.periodEnd,
    grossFares,
    platformFeesGross,
    platformFeesGst: explicitPlatformGst || (platformFeesGross > 0 ? parseMoney((platformFeesGross / 11).toFixed(2)) : 0),
    tollsGross,
    tipsAndBonuses,
    confidence,
    rawText,
    notes,
  };
}

type TextItemLike = { str?: string; transform?: ArrayLike<number> };

function textItemsToLines(items: unknown[]) {
  const positioned = items
    .map((item) => {
      const textItem = item as TextItemLike;
      const str = textItem.str || "";
      const transform = textItem.transform;
      return {
        str,
        x: transform ? Number(transform[4]) || 0 : 0,
        y: transform ? Number(transform[5]) || 0 : 0,
      };
    })
    .filter((item) => item.str.trim());

  if (positioned.length === 0) return "";
  if (positioned.every((item) => item.x === 0 && item.y === 0)) {
    return positioned.map((item) => item.str).join(" ");
  }

  const rows: Array<{ y: number; items: typeof positioned }> = [];
  for (const item of positioned.sort((a, b) => b.y - a.y || a.x - b.x)) {
    const row = rows.find((candidate) => Math.abs(candidate.y - item.y) <= 3);
    if (row) {
      row.items.push(item);
    } else {
      rows.push({ y: item.y, items: [item] });
    }
  }

  return rows
    .map((row) =>
      row.items
        .sort((a, b) => a.x - b.x)
        .map((item) => item.str)
        .join(" ")
        .replace(/\s+/g, " ")
        .trim(),
    )
    .filter(Boolean)
    .join("\n");
}

function normalizeText(text: string) {
  return text.replace(/\s+/g, " ").replace(/\u00a0/g, " ").trim();
}

function normalizeLines(text: string) {
  return text
    .replace(/\u00a0/g, " ")
    .split(/\r?\n/)
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

function pickFieldAmount(lines: string[], text: string, labels: RegExp[]) {
  for (const label of labels) {
    const amountFromLine = pickAmountFromLines(lines, label);
    if (amountFromLine > 0) return amountFromLine;
  }

  for (const label of labels) {
    const source = label.source.replace(/^\^|\$$/g, "");
    const match = text.match(new RegExp(`(?:${source})[^\\d$-]{0,80}(${moneyPattern})`, "i"));
    if (match?.[1]) return parseMoney(match[1]);
  }
  return 0;
}

const moneyPattern = "\\(?-?\\$?\\s?\\d[\\d,]*(?:\\.\\d{2})?\\)?";

function pickAmountFromLines(lines: string[], label: RegExp) {
  for (const line of lines) {
    if (!label.test(line)) continue;
    const amounts = line.match(new RegExp(moneyPattern, "g")) || [];
    const parsed = amounts.map(parseMoney).filter((amount) => amount > 0);
    if (parsed.length > 0) return parsed[parsed.length - 1];
  }
  return 0;
}

function pickFirstAmountAfterLabel(lines: string[], label: RegExp) {
  for (const line of lines) {
    const match = line.match(label);
    if (!match?.index && match?.index !== 0) continue;
    const afterLabel = line.slice(match.index + match[0].length);
    const amounts = afterLabel.match(new RegExp(moneyPattern, "g")) || [];
    const parsed = amounts.map(parseMoney).filter((amount) => amount > 0);
    if (parsed.length > 0) return parsed[0];
  }
  return 0;
}

function sumFieldAmounts(lines: string[], labels: RegExp[]) {
  const seen = new Set<string>();
  let total = 0;
  for (const label of labels) {
    for (const line of lines) {
      if (!label.test(line) || seen.has(line)) continue;
      const amounts = line.match(new RegExp(moneyPattern, "g")) || [];
      const parsed = amounts.map(parseMoney).filter((amount) => amount > 0);
      if (parsed.length > 0) {
        total += parsed[parsed.length - 1];
        seen.add(line);
      }
    }
  }
  return parseMoney(total.toFixed(2));
}

function detectProvider(text: string, fileName: string): ExtractedTaxSummary["provider"] {
  const source = `${text} ${fileName}`;
  if (/uber/i.test(source)) return "uber";
  if (/didi/i.test(source)) return "didi";
  if (/manual/i.test(fileName)) return "manual";
  return "other";
}

function parsePeriod(text: string, fileName: string) {
  const monthRangePattern =
    /(\d{1,2})\s*(?:-|to|through)\s*(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(\d{4})/i;
  const datePattern =
    /(\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{4})\s*(?:-|to|through)\s*(\d{1,2}\s+(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{4})/i;
  const isoPattern = /(\d{4}-\d{2}-\d{2})\s*(?:-|to|through)\s*(\d{4}-\d{2}-\d{2})/i;
  const numericMonthPattern = /(?:period|month)?\s*(0?[1-9]|1[0-2])\s*\/\s*(\d{4})/i;
  const source = `${text} ${fileName}`;
  const isoMatch = source.match(isoPattern);
  if (isoMatch) return { periodStart: isoMatch[1], periodEnd: isoMatch[2] };

  const match = source.match(datePattern);
  if (match) {
    return {
      periodStart: toDateInputValue(match[1]),
      periodEnd: toDateInputValue(match[2]),
    };
  }

  const monthRangeMatch = source.match(monthRangePattern);
  if (monthRangeMatch) {
    const startDay = Number(monthRangeMatch[1]);
    const endDay = Number(monthRangeMatch[2]);
    const month = monthIndex(monthRangeMatch[3]);
    const year = Number(monthRangeMatch[4]);
    return {
      periodStart: toIso(new Date(year, month, startDay)),
      periodEnd: toIso(new Date(year, month, endDay)),
    };
  }

  const numericMonthMatch = source.match(numericMonthPattern);
  if (numericMonthMatch) {
    const month = Number(numericMonthMatch[1]) - 1;
    const year = Number(numericMonthMatch[2]);
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return { periodStart: toIso(start), periodEnd: toIso(end) };
  }

  const quarterMatch = source.match(/(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s*[-_ ]\s*(\d{4})/i);
  if (quarterMatch) {
    const month = monthIndex(quarterMatch[1]);
    const year = Number(quarterMatch[2]);
    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);
    return { periodStart: toIso(start), periodEnd: toIso(end) };
  }

  return { periodStart: "", periodEnd: "" };
}

function toDateInputValue(value: string) {
  const parsed = parseHumanDate(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return toIso(parsed);
}

function toIso(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function monthIndex(value: string) {
  return ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"].findIndex((month) =>
    value.toLowerCase().startsWith(month),
  );
}

function parseHumanDate(value: string) {
  const match = value
    .trim()
    .match(/^(\d{1,2})\s+(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+(\d{4})$/i);
  if (!match) return new Date(value);
  const day = Number(match[1]);
  const month = monthIndex(match[2]);
  const year = Number(match[3]);
  return new Date(year, month, day);
}
